/**
 * Step 1: type a legacy ticket number and look it up (read-only).
 *
 * While legacy access is not connected the lookup cannot succeed, so the
 * button is disabled and the connect banner above explains why — and a lookup
 * refused for that reason shows only the banner, never a second, red error.
 */

import { Icon, Search } from "@norican/icons";
import { Button, Field, Form, Input } from "@norican/react";
import { useState } from "react";

import { ErrorState, normaliseApiError, SectionCard } from "@/components/common";
import { useT } from "@/i18n";
import type { LegacySummary } from "@/lib/api";

import { useLegacyLookup, useMigrateStatus } from "../api";
import {
  isLegacyNumber,
  LEGACY_NOT_CONNECTED,
  normaliseLegacyNumber,
} from "../lib/migrate-helpers";

export interface LookupStepProps {
  initialNumber?: string;
  onFound: (summary: LegacySummary) => void;
}

export function LookupStep({ initialNumber = "", onFound }: LookupStepProps) {
  const { t } = useT("tickets");
  const [number, setNumber] = useState(initialNumber);
  const [invalid, setInvalid] = useState(false);
  const lookup = useLegacyLookup();
  const status = useMigrateStatus();
  const notConnected = status.data?.connected === false;

  const handleSubmit = () => {
    const normalised = normaliseLegacyNumber(number);
    if (!isLegacyNumber(normalised)) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    lookup.mutate(normalised, {
      onSuccess: onFound,
      onError: (error) => {
        // The status the banner reads may be stale; refresh it so the banner
        // is what explains this refusal.
        if (normaliseApiError(error).code === LEGACY_NOT_CONNECTED) {
          void status.refetch();
        }
      },
    });
  };

  return (
    <SectionCard title={t("migrate.lookup.title")}>
      <Form className="flex flex-col gap-4" onFormSubmit={handleSubmit}>
        <Field
          name="legacy_number"
          label={t("migrate.lookup.label")}
          hint={t("migrate.lookup.hint")}
          error={invalid ? t("migrate.lookup.invalid") : undefined}
          required
        >
          <Input
            name="legacy_number"
            value={number}
            placeholder="S-00004954"
            onChange={(event) => {
              setNumber(event.target.value);
              // Otherwise the error blocks every later submit (the Form won't).
              setInvalid(false);
            }}
          />
        </Field>
        {lookup.isError && !notConnected ? (
          <ErrorState variant="inline" error={lookup.error} />
        ) : null}
        <div>
          <Button
            type="submit"
            loading={lookup.isPending}
            disabled={lookup.isPending || notConnected}
          >
            <Icon icon={Search} size="sm" />
            {lookup.isPending ? t("migrate.lookup.reading") : t("migrate.lookup.submit")}
          </Button>
        </div>
      </Form>
    </SectionCard>
  );
}
